/**
 * The patient's view of the shared board.
 *
 * Public: no account, no app shell, no navigation — the token in the URL is the
 * whole authorisation. What they get is the identical board and the identical
 * palette; what they do not get is any way to manage the map, or any sight of
 * the practice's record of them.
 *
 * They are asked their name first. The board labels every element with the
 * hand that placed it, and "Patient" is a label that tells the therapist
 * nothing they did not already know.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";

import { usePatientMap } from "../../application/usePublicSystemicMap";
import MapBoard from "../components/MapBoard";
import NameGate from "../components/NameGate";

const NAME_STORAGE_PREFIX = "systemicMap:name:";

const rememberedName = (token: string): string | null => {
    try {
        return localStorage.getItem(`${NAME_STORAGE_PREFIX}${token}`);
    } catch {
        // A private window, or storage blocked. Asking again is a small cost.
        return null;
    }
};

const rememberName = (token: string, name: string): void => {
    try {
        localStorage.setItem(`${NAME_STORAGE_PREFIX}${token}`, name);
    } catch {
        /* not fatal — see above */
    }
};

export default function PatientSystemicMapPage() {
    const { token } = useParams<{ token: string }>();
    // Set when the board is framed inside a call: the name the patient joined
    // the call under. They have said who they are once already.
    const [searchParams] = useSearchParams();
    const callName = searchParams.get("name")?.trim() || null;
    const [name, setName] = useState<string | null>(() =>
        callName ?? (token ? rememberedName(token) : null),
    );

    if (!token) return null;

    // Asked once per link: a reload should not interrogate someone who is
    // already halfway through drawing their family.
    if (!name) {
        return (
            <NameGate
                onSubmit={(chosen) => {
                    rememberName(token, chosen);
                    setName(chosen);
                }}
            />
        );
    }

    return <PatientBoard token={token} name={name} />;
}

function PatientBoard({ token, name }: { token: string; name: string }) {
    const { t } = useTranslation();
    const map = usePatientMap(token, name);

    // The therapist took the link back while this board was open. Said here
    // rather than left to the next refused write: someone would otherwise keep
    // drawing into a document that will keep none of it.
    if (map.presence?.revoked) {
        return (
            <Frame>
                <p role="alert" className="text-sm text-status-critical">
                    {t("systemicMap.patient.revoked")}
                </p>
            </Frame>
        );
    }

    if (map.tokenError) {
        return (
            <Frame>
                <p role="alert" className="text-sm text-status-critical">
                    {t(`systemicMap.patient.${map.tokenError}`)}
                </p>
            </Frame>
        );
    }

    if (!map.state) {
        return (
            <Frame>
                <p
                    role="status"
                    className="flex items-center gap-2 text-sm text-content-secondary"
                >
                    <Loader2
                        size={16}
                        aria-hidden="true"
                        className="animate-spin motion-reduce:animate-none"
                    />
                    {t("systemicMap.patient.joining")}
                </p>
            </Frame>
        );
    }

    return (
        <div className="flex h-[100dvh] w-screen flex-col gap-3 overflow-hidden bg-surface-base p-3">
            <header className="flex items-center gap-3">
                <img
                    src="/images/logo/icon.png"
                    alt="Alchely"
                    width={26}
                    height={26}
                    className="shrink-0 rounded-md"
                />
                <div className="min-w-0">
                    <h1 className="truncate text-base font-semibold text-content-primary">
                        {/* Named for what it is: the patient is looking at a
                            genogram or an ecomap, not at "a shared board". */}
                        {t(`systemicMap.kind.${map.state.kind}`)}
                    </h1>
                    <p className="truncate text-xs text-content-secondary">
                        {t("systemicMap.patient.help")}
                    </p>
                </div>
            </header>

            <div className="min-h-0 flex-1">
                <MapBoard
                    state={map.state}
                    saveStatus={map.saveStatus}
                    onSubmit={map.submit}
                    onUndo={map.undo}
                    onRedo={map.redo}
                    canUndo={map.canUndo}
                    canRedo={map.canRedo}
                    presence={map.presence}
                    viewer={{
                        type: "participant",
                        id: map.participantId ?? undefined,
                    }}
                />
            </div>
        </div>
    );
}

function Frame({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-[100dvh] w-screen items-center justify-center bg-surface-base p-6">
            <div className="max-w-md text-center">{children}</div>
        </div>
    );
}
